import { compact, fromPairs, head, isArray, keys, take, toLower } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getSampleCsvFile } from '~/api/automation';
import { showToast } from '~/components/Shared/EcToast/showToast';
import { Box } from '~/eds/src/components/Box/Box';
import { SummaryCard } from '~/eds/src/components/Card/subcomponents/SummaryCard';
import { EmptyPage } from '~/eds/src/components/EmptyPage/EmptyPage';
import { useDeletePermanentModal } from '~/eds/src/components/hooks/useDeletePermanentModal';
import { useModal } from '~/eds/src/components/hooks/useModal';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Text } from '~/eds/src/components/Text/Text';
import { TextInput } from '~/eds/src/components/TextInput/TextInput';
import { AutomationStageType } from '~/enums/AutomationStageType';
import { QueryParamType } from '~/enums/QueryParamType';
import { TrainingStage } from '~/enums/TrainingStage';
import { TrainingStatus } from '~/enums/TrainingStatus';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { FormattedMessage, useIntl } from 'react-intl';
import { useFormatNumber } from '~/i18n/hooks/useFormatNumber/useFormatNumber';
import { buildQuery } from '~/redux/api/methods/search';
import { api } from '~/redux/api/createApi';
import { slices } from '~/redux/slices';
import { useRouting } from '~/routing/useRouting';
import { ERROR, SUCCESS } from '~/types/toast.types';
import Alert from '~/ui/components/Alert/Alert';
import Link from '~/ui/components/Link/Link';
import LoadingContainer from '~/ui/components/LoadingContainer/LoadingContainer';
import { removeQueryParam } from '~/utils/browser';
import { getPageSearchQueryByKey } from '~/utils/searchQuery';
import { testIsAdmin } from '~/utils/user';

import AutomationHubSearchingFilters from '../AutomationHubSearchFilters/AutomationHubSearchingFilters';
import {
  ACCURACY_METRIC_TRAINING_STAGES,
  DEFAULT_PHRASES,
  DEFAULT_RULES,
  getTrainingStages,
  LABELING_TRAINING_STAGES,
  POSITIVE_LABELS,
} from '../constants';
import {
  calculateF1Score,
  getCurrentProgress,
  getCurrentStage,
  verCmp,
} from '../util';
import AccuracyMetrics from './Content/AccuracyMetrics';
import FilterSummary from './Content/FilterSummary';
import PredictiveInfo from './Content/PredictiveInfo';
import ProgressBar from './Content/ProgressBar';
import SampleTable from './Content/SampleTable';

const AutomationHubGuidedAiPage = () => {
  const dispatch = useDispatch();
  const currentUser = useCurrentUser();
  const isAdmin = testIsAdmin(currentUser);
  const intl = useIntl();
  const formatNumber = useFormatNumber();
  const TRAINING_STAGES = getTrainingStages(intl);
  const retrainText = intl.formatMessage({
    id: 'AutomationHubGuidedAiPage.retrainButton',
    defaultMessage: 'Retrain',
    description: 'Button text for retraining',
  });

  const publishText = intl.formatMessage({
    id: 'AutomationHubGuidedAiPage.publishButton',
    defaultMessage: 'Publish',
    description: 'Button text for publishing',
  });

  const publishAndOverwriteText = intl.formatMessage({
    id: 'AutomationHubGuidedAiPage.publishAndOverwriteButton',
    defaultMessage: 'Publish and Overwrite',
    description: 'Button text for publishing and overwriting',
  });

  const cancelTrainingText = intl.formatMessage({
    id: 'AutomationHubGuidedAiPage.cancelTrainingText',
    defaultMessage: 'Cancel Training',
    description: 'Button text for canceling training',
  });

  const deleteModelText = intl.formatMessage({
    id: 'AutomationHubGuidedAiPage.deleteModelText',
    defaultMessage: 'Delete Model',
    description: 'Button text for deleting a model',
  });

  const model = useSelector(slices.automation.selectors.selectAutomationModel);

  const filters = useSelector(
    slices.automation.selectors.selectAutomationFilters,
  );

  const fields = useSelector(
    slices.automation.selectors.selectAutomationFields,
  );

  const guidedAiForm = useSelector(
    slices.automation.selectors.selectAutomationQuickAiForm,
  );

  const isRetraining =
    model.stage === TrainingStage.Predicted ||
    (model?.allVersions?.length || 0) > 1;

  const availableSamples =
    model?.current?.unlabeledFeedback &&
    isArray(model.current.unlabeledFeedback)
      ? model.current.unlabeledFeedback.filter((sample) => !!sample.Text)
      : [];

  const [samples, setSamples] = useState([]);
  const [retrainGuidedAIConfirmation, setRetrainGuidedAIConfirmation] =
    useState('');

  const { location, navigate } = useRouting();

  const clauseId = getPageSearchQueryByKey(QueryParamType.clauseId, model.id);
  const totalSamples = isArray(model?.current?.userInput?.fullTrainingData)
    ? model?.current?.userInput?.fullTrainingData
    : model?.current?.userInput?.userUploadData;

  const shouldWaitForStatus = model && model.stage === TrainingStage.Waiting;
  const hasStageInitialized =
    model.stage !== TrainingStage.Empty && !shouldWaitForStatus;

  const { data: trainingStatus, isFetching: isFetchingTrainingStatus } =
    api.endpoints.getTrainingStatus.useQuery(
      { clauseId },
      { skip: !clauseId, pollingInterval: 3000 },
    );

  const { data: filtersData, isFetching: isFetchingFiltersData } =
    api.endpoints.getFiltersData.useQuery({ clauseId }, { skip: !clauseId });

  const { data: idf } = api.endpoints.getIdentifications.useQuery(
    { name: model.name },
    { skip: !model.name },
  );

  const hasPredictionRun = trainingStatus && !!model?.current?.prediction?.id;

  const shouldShowDownloadTrainingDataAction = ![
    TrainingStage.Empty,
    TrainingStage.FirstRoundInProgress,
    TrainingStage.FirstRoundLabeling,
  ].includes(model.stage);

  const shouldShowModelStatusAction = ACCURACY_METRIC_TRAINING_STAGES.includes(
    model.stage,
  );

  const { data: predictionStatus, isFetching: isFetchingPredictionStatus } =
    api.endpoints.getPredictionStatus.useQuery(
      { requestId: model?.current?.prediction?.id },
      { skip: !model?.current?.prediction?.id, pollingInterval: 8000 },
    );

  const [createRetraining, createRetrainingResult] =
    api.endpoints.createRetraining.useMutation();
  const {
    isError: isCreateRetrainingError,
    isSuccess: isCreateRetrainingSuccess,
    isLoading: isCreateRetrainingLoading,
  } = createRetrainingResult;

  const [publishTrainedModel, publishTrainedModelResult] =
    api.endpoints.publishTrainedModel.useMutation();
  const {
    isError: isPublishTrainedModelError,
    isSuccess: isPublishTrainedModelSuccess,
    isLoading: isPublishTrainedModelLoading,
  } = publishTrainedModelResult;

  const [updatePredictionFilters, updatePredictionFiltersResults] =
    api.endpoints.updatePredictionFilters.useMutation();
  const {
    isError: isUpdatePredictionFiltersError,
    isSuccess: isUpdatePredictionFiltersSuccess,
    isLoading: isUpdatePredictionFiltersLoading,
  } = updatePredictionFiltersResults;

  const [deleteTrainingModel, deleteTrainingModelResult] =
    api.endpoints.deleteTrainingModel.useMutation();
  const {
    isError: isDeleteTrainingModelError,
    isSuccess: isDeleteTrainingModelSuccess,
    isLoading: isDeleteTrainingModelLoading,
  } = deleteTrainingModelResult;

  const [cancelTraining, cancelTrainingResult] =
    api.endpoints.cancelTraining.useMutation();
  const { isError: isCancelTrainingError, isSuccess: isCancelTrainingSuccess } =
    cancelTrainingResult;

  const [predictionModal, predictionModalShow, predictionModalHide] = useModal({
    disableHideOnEscape: true,
    children: (
      <Layout direction="column" spacing={2}>
        <Text>
          {intl.formatMessage(
            {
              id: 'AutomationHubGuidedAiPage.modalIntroText',
              defaultMessage:
                'You trained your model using All Types of contracts. What contracts would you like your model to tag with the {name} clause?',
              description: 'Intro text for prediction filter modal',
            },
            {
              name: model.name,
            },
          )}
        </Text>
        <AutomationHubSearchingFilters prediction />
      </Layout>
    ),
    primaryAction: {
      isLoading: isUpdatePredictionFiltersLoading,
      text: intl.formatMessage({
        id: 'AutomationHubGuidedAiPage.modalDoneButton',
        defaultMessage: 'Done',
        description: 'Text for done button in prediction modal',
      }),
      variant: 'primary',
      onClick: () => {
        const filterQuery = buildQuery({
          filters: filters.filter((f) => !!f.operatorId),
          fields,
          booleanQuery: '',
        });

        return updatePredictionFilters({
          clauseId,
          predictionFilters: filterQuery,
        });
      },
    },
    title: intl.formatMessage({
      id: 'AutomationHubGuidedAiPage.modalTitle',
      defaultMessage: 'Apply Model to Specific Contracts',
      description: 'Title for prediction filter modal',
    }),
    onCancel: () =>
      dispatch(slices.automation.actions.resetAutomationFilters()),
    onHide: () => dispatch(slices.automation.actions.resetAutomationFilters()),
  });

  const [retrainModal, retrainModalShow, retrainModalHide] = useModal({
    title: intl.formatMessage({
      id: 'AutomationHubGuidedAiPage.retrainModalTitle',
      defaultMessage: 'Warning',
      description: 'Title for retrain modal',
    }),
    primaryAction: {
      text: intl.formatMessage({
        id: 'AutomationHubGuidedAiPage.retrainModalPrimaryAction',
        defaultMessage: 'I understand the risk',
        description: 'Text for primary action button in retrain modal',
      }),
      variant: 'primary',
      onClick: () => {
        retrainModalHide();
        dispatch(
          slices.automation.actions.setAutomationManageStage(
            AutomationStageType.GuidedAiForm,
          ),
        );
      },
    },
    cancelText: intl.formatMessage({
      id: 'AutomationHubGuidedAiPage.retrainModalCancelAction',
      defaultMessage: 'Never mind',
      description: 'Text for cancel button in retrain modal',
    }),
    children: (
      <Text>
        {intl.formatMessage({
          id: 'AutomationHubGuidedAiPage.retrainModalDescription',
          defaultMessage:
            'You are about to start another round of training. Just a reminder, once you start training, your current identifications will be removed and replaced with the new changes.',
          description: 'Description for retrain modal',
        })}
      </Text>
    ),
  });

  const [
    deleteGuidedAiModal,
    deleteGuidedAiModalShow,
    deleteGuidedAiModalHide,
  ] = useDeletePermanentModal({
    disableHideOnEscape: true,
    children: (
      <Layout direction="column" spacing={4}>
        <Text>
          {intl.formatMessage(
            {
              id: 'AutomationHubGuidedAiPage.deleteModelModalDescription',
              defaultMessage:
                'This Clause has {totalSamples} Samples and {identifications} identifications. Deleting this clause will permanently delete all its Samples and their identifications. You won’t be able to revert this action.',
              description: 'Description for delete model modal',
            },
            {
              totalSamples: totalSamples?.length ?? 0,
              identifications: idf?.smartClauseTotalIdentifications ?? 0,
            },
          )}
        </Text>
      </Layout>
    ),
    isDeleting: isDeleteTrainingModelLoading,
    confirmText: intl.formatMessage({
      id: 'AutomationHubGuidedAiPage.deleteModelModalConfirmText',
      defaultMessage: 'DELETE',
      description: 'Text for confirm button in delete model modal',
    }),
    onDelete: () => {
      deleteTrainingModel({ clauseId });
    },
    title: intl.formatMessage(
      {
        id: 'AutomationHubGuidedAiPage.deleteModelModalTitle',
        defaultMessage: 'Delete model for Clause: {name}',
        description: 'Title for delete model modal',
      },
      {
        name: model.name,
      },
    ),
  });

  const [
    cancelTrainingModal,
    showCancelTrainingModal,
    hideCancelTrainingModal,
  ] = useModal({
    title: cancelTrainingText,
    children: (
      <Layout direction="column" spacing={4}>
        <Text>
          {intl.formatMessage({
            id: 'AutomationHubGuidedAiPage.cancelTrainingModalDescription',
            defaultMessage:
              'By cancelling, the training you are leaving the existing model intact including all of the identifications.',
            description: 'Description for cancel training modal',
          })}
        </Text>
      </Layout>
    ),
    primaryAction: {
      text: cancelTrainingText,
      variant: 'primary',
      onClick: () => {
        hideCancelTrainingModal();
        if (isRetraining) {
          cancelTraining({ clauseId });
        } else {
          deleteTrainingModel({ clauseId });
        }
      },
    },
    cancelText: 'Keep Training',
  });

  useEffect(() => {
    if (isCreateRetrainingSuccess) {
      showToast(
        SUCCESS,
        intl.formatMessage(
          {
            id: 'AutomationHubGuidedAiPage.retrainSuccess',
            defaultMessage: 'Retrain {modelName} successfully.',
            description: 'Toast message for successful retraining of a model',
          },
          {
            modelName: guidedAiForm.name,
          },
        ),
      );

      const prevIterVersions = keys(model.current?.pastIterations);
      switch (prevIterVersions.length) {
        case 1:
          dispatch(
            slices.automation.actions.setAutomationModel({
              ...model,
              stage: TrainingStage.SecondRoundInProgress,
            }),
          );
          break;
        case 2:
          dispatch(
            slices.automation.actions.setAutomationModel({
              ...model,
              stage: TrainingStage.ThirdRoundInProgress,
            }),
          );
          break;
        case 3:
          dispatch(
            slices.automation.actions.setAutomationModel({
              ...model,
              stage: TrainingStage.FourthRoundInProgress,
            }),
          );
          break;
        default:
          break;
      }
    }
    if (isCreateRetrainingError) {
      showToast(
        ERROR,
        intl.formatMessage(
          {
            id: 'AutomationHubGuidedAiPage.retrainError',
            defaultMessage: 'Retrain {modelName} failed. Please try again.',
            description: 'Toast message for failed retraining of a model',
          },
          {
            modelName: guidedAiForm.name,
          },
        ),
      );
    }
  }, [isCreateRetrainingSuccess, isCreateRetrainingError]);

  useEffect(() => {
    if (isPublishTrainedModelSuccess) {
      showToast(
        SUCCESS,
        intl.formatMessage(
          {
            id: 'AutomationHubGuidedAiPage.publishSuccess',
            defaultMessage: 'Publish model {modelName} successfully.',
            description: 'Toast message for successful model publishing',
          },
          {
            modelName: guidedAiForm.name,
          },
        ),
      );

      dispatch(
        slices.automation.actions.setAutomationModel({
          ...model,
          stage: TrainingStage.Publishing,
        }),
      );
    }
    if (isPublishTrainedModelError) {
      showToast(
        ERROR,
        intl.formatMessage(
          {
            id: 'AutomationHubGuidedAiPage.publishError',
            defaultMessage:
              'Publish model {modelName} failed. Please try again.',
            description: 'Toast message for failed model publishing',
          },
          {
            modelName: guidedAiForm.name,
          },
        ),
      );
    }
  }, [isPublishTrainedModelSuccess, isPublishTrainedModelError]);

  useEffect(() => {
    if (isUpdatePredictionFiltersSuccess) {
      const message = intl.formatMessage({
        id: 'AutomationHubGuidedAiPage.updatePredictionFilterSuccess',
        defaultMessage: 'Update prediction filter successfully.',
        description: 'Toast message for successful prediction filter update',
      });
      showToast(SUCCESS, message);

      predictionModalHide();
      dispatch(slices.automation.actions.resetAutomationFilters());
    }
    if (isUpdatePredictionFiltersError) {
      const message = intl.formatMessage({
        id: 'AutomationHubGuidedAiPage.updatePredictionFilterFailed',
        defaultMessage: 'Update prediction filter failed. Please try again.',
        description: 'Toast message for failed prediction filter update',
      });
      showToast(ERROR, message);
    }
  }, [isUpdatePredictionFiltersSuccess, isUpdatePredictionFiltersError]);

  useEffect(() => {
    if (isDeleteTrainingModelSuccess) {
      const message = `${isRetraining ? deleteModelText : cancelTrainingText} ${
        model.name
      } successful.`;
      showToast(SUCCESS, message);

      dispatch(slices.automation.actions.deleteAutomationModel());
      deleteGuidedAiModalHide();
      removeQueryParam(location, navigate, QueryParamType.clauseId);
    }
    if (isUpdatePredictionFiltersError) {
      const message = `${isRetraining ? deleteModelText : cancelTrainingText} ${
        model.name
      } failed. Please try again.`;

      showToast(ERROR, message);
    }
  }, [isDeleteTrainingModelSuccess, isDeleteTrainingModelError]);

  useEffect(() => {
    if (isCancelTrainingSuccess) {
      const message = `Cancel Training ${model.name} successful.`;
      showToast(SUCCESS, message);
    }
    if (isCancelTrainingError) {
      const message = `Cancel Training ${model.name} failed. Please try again.`;
      showToast(ERROR, message);
    }
  }, [isCancelTrainingSuccess, isCancelTrainingError]);

  useEffect(() => {
    if (!isFetchingTrainingStatus && trainingStatus) {
      const version = model.version
        ? model.version.split('.').slice(0, 2).join('.')
        : null;

      const allModelVersions = keys(trainingStatus.modelVersions);
      const modelCurVersion = allModelVersions.sort(verCmp)[0];

      const current = {
        version: version || modelCurVersion,
        ...trainingStatus.modelVersions[version || modelCurVersion],
      };

      setSamples(current.userInput?.userUploadData ?? []);

      const pastIterVersionsInOrder = keys(current?.pastIterations).sort(
        verCmp,
      );
      // EKP-13825: this change will take the most 3 recent iterations since
      // there could have more than three iterations during model migration
      const prevIterVersions = take(pastIterVersionsInOrder, 3);
      const iterLastVersion = head(pastIterVersionsInOrder);
      const pastIter = current?.pastIterations?.[iterLastVersion];

      const name = trainingStatus.provisionName;
      const description = trainingStatus.description;

      const isTrainingFailed = [
        TrainingStatus.PUBLISH_ERROR,
        TrainingStatus.TRAINING_ERROR,
      ].includes(current?.status);

      if (isTrainingFailed) {
        dispatch(
          slices.automation.actions.setAutomationModel({
            ...model,
            allVersions: allModelVersions,
            current,
            name,
            description,
            id: clauseId,
            stage: TrainingStage.Error,
          }),
        );
        return;
      }

      const isTrainingInProgress =
        current?.status === TrainingStatus.IN_PROGRESS;

      const f1ScoreCurrent = calculateF1Score(current?.accuracyMetrics) ?? 0;
      const f1ScorePastIter = calculateF1Score(pastIter?.accuracyMetrics) ?? 0;

      const isScoreLow = f1ScoreCurrent < 0.75;
      const isScoreOverExp = !isScoreLow && f1ScoreCurrent < 0.9;
      const isScoreIdeal = f1ScoreCurrent >= 0.9;
      const isScoreUp = f1ScorePastIter < f1ScoreCurrent;
      const isScoreDown = f1ScorePastIter >= f1ScoreCurrent;

      const isAwaitingUserLabels =
        current?.status === TrainingStatus.AWAITING_LABELS;
      const isAwaitingDecision =
        current?.status === TrainingStatus.AWAITING_DECISION;

      const isReadyToDeploy =
        current?.status === TrainingStatus.READY_TO_DEPLOY;
      const isPublishInProgress =
        current?.status === TrainingStatus.PUBLISH_IN_PROGRESS;

      const isPublishing =
        !!model?.isPublishing || isReadyToDeploy || isPublishInProgress;
      const isPublished = current?.status === TrainingStatus.PUBLISH_COMPLETED;

      const predictionProgress = predictionStatus?.status[clauseId];
      const isPredicting =
        predictionProgress && parseFloat(predictionProgress) < 100;
      const isPredicted = predictionProgress && !isPredicting;

      const stage = getCurrentStage(
        prevIterVersions,
        {
          isTrainingInProgress,
          isAwaitingUserLabels,
          isAwaitingDecision,
        },
        {
          isScoreLow,
          isScoreOverExp,
          isScoreIdeal,
          isScoreUp,
          isScoreDown,
        },
        {
          isPublishing,
          isPublished,
          isPredicting,
          isPredicted,
        },
      );

      const progress = getCurrentProgress({
        stage,
        iters: prevIterVersions?.length ?? 0,
        createdAt: current?.iterationCreatedAt,
        updatedAt: current?.versionUpdatedAt,
        status: current?.status,
        prediction: predictionProgress,
        score: f1ScoreCurrent,
        intl,
        formatNumber,
      });

      if (shouldWaitForStatus) {
        dispatch(
          slices.automation.actions.setAutomationModel({
            ...model,
            stage: TrainingStage.Empty,
          }),
        );
      } else {
        dispatch(
          slices.automation.actions.setAutomationModel({
            ...model,
            allVersions: allModelVersions,
            id: clauseId,
            current: { ...model.current, ...current },
            name,
            description,
            progress,
            stage,
          }),
        );
      }
    }
  }, [
    trainingStatus,
    isFetchingTrainingStatus,
    predictionStatus,
    isFetchingPredictionStatus,
  ]);

  useEffect(() => {
    if (!isFetchingFiltersData && filtersData) {
      const version = model.version
        ? model.version.split('.').slice(0, 2).join('.')
        : null;

      const allModelVersions = keys(filtersData);
      const modelCurVersion = allModelVersions.sort(verCmp)[0];

      const filterVersionData = filtersData[version || modelCurVersion];

      const predictionFilter = filterVersionData?.predictionFilter || [];
      const trainingFilter = filterVersionData?.trainingFilter || [];

      dispatch(
        slices.automation.actions.setAutomationModel({
          ...model,
          current: {
            ...model.current,
            filter: { predictionFilter, trainingFilter },
          },
        }),
      );
    }
  }, [isFetchingFiltersData, filtersData]);

  const onPutLabel = () => {
    const feedbacks = model.current?.labeledFeedback;

    const samples = model.current.unlabeledFeedback.map((s) => {
      const feedback = feedbacks.find(
        (f) => String(f?.sampleID) === String(s?.sampleID),
      );

      if (!feedback) return { ...s, Text: s.Text, Label: s.Label };

      const label = POSITIVE_LABELS.includes(toLower(feedback.Label))
        ? 'yes'
        : 'no';
      return { ...s, Text: s.Text, Label: label };
    });

    dispatch(
      slices.automation.actions.setAutomationGuidedAiDataForm({
        ...guidedAiForm,
        id: clauseId,
        samples,
        name: trainingStatus.provisionName,
        description: trainingStatus.description,
        metadata: model.current.metadata,
      }),
    );

    dispatch(
      slices.automation.actions.setAutomationManageStage(
        AutomationStageType.GuidedAiReview,
      ),
    );
  };

  const onRetrain = () => {
    const rules = model.current.userInput?.rules ?? DEFAULT_RULES;
    const phrases = model.current.userInput?.phrases ?? DEFAULT_PHRASES;

    return createRetraining({ clauseId, config: { rules, phrases } });
  };

  const onPublish = async () => {
    setRetrainGuidedAIConfirmation('');
    return publishTrainedModel({ clauseId });
  };

  const onDownloadSamples = () => {
    getSampleCsvFile({ clauseId, version: model.current?.version });
  };

  const handlePublish = () => {
    if (isRetraining) {
      showRetrainGuidedAIWarning();
    } else {
      onPublish();
    }
  };

  const [
    retrainGuidedAIWarning,
    showRetrainGuidedAIWarning,
    hideRetrainGuidedAIWarning,
  ] = useModal({
    title: publishAndOverwriteText,
    children: (
      <Layout direction="column" spacing={4}>
        <Text>
          <FormattedMessage
            id="AutomationHubGuidedAiPage.publishOverwriteWarning"
            defaultMessage="Once published, this improved model will identify new matches to the <strong>{model?.name} Clause</strong>, and it will overwrite the previous results. You won't be able to revert this action."
            description="Warning for publish and overwrite confirmation modal"
            values={{
              strong: (chunks) => <strong>{chunks}</strong>,
            }}
          />
        </Text>
        <Text>
          {intl.formatMessage({
            id: 'AutomationHubGuidedAiPage.publishOverwritePrompt',
            defaultMessage:
              'If you are sure, please type "OVERWRITE" in the box below',
            description: 'Prompt for publish and overwrite confirmation modal',
          })}
        </Text>
        <TextInput
          name="confirm-retrain"
          value={retrainGuidedAIConfirmation}
          onChange={setRetrainGuidedAIConfirmation}
        />
      </Layout>
    ),
    primaryAction: {
      text: publishAndOverwriteText,
      onClick: () => {
        hideRetrainGuidedAIWarning();
        onPublish();
      },
      variant: 'danger',
      disabled: retrainGuidedAIConfirmation !== 'OVERWRITE',
    },
    onCancel: () => {
      setRetrainGuidedAIConfirmation('');
    },
  });

  const actions = {
    ...fromPairs(
      [
        TrainingStage.Empty,
        TrainingStage.FirstRoundInProgress,
        TrainingStage.SecondRoundInProgress,
        TrainingStage.ThirdRoundInProgress,
        TrainingStage.FourthRoundInProgress,
      ].map((key) => [key, []]),
    ),
    ...fromPairs(
      [
        TrainingStage.FirstRoundLabeling,
        TrainingStage.SecondRoundLabeling,
        TrainingStage.ThirdRoundLabeling,
        TrainingStage.FourthRoundLabeling,
      ].map((key) => [
        key,
        [
          {
            text: intl.formatMessage({
              id: 'AutomationHubGuidedAiPage.giveFeedbackOnSamplesButton',
              defaultMessage: 'Give Feedback on Samples',
              description: 'Button text for giving feedback on samples',
            }),
            level: 'primary',
            onClick: onPutLabel,
          },
        ],
      ]),
    ),
    ...fromPairs(
      [TrainingStage.FirstRoundF1Low, TrainingStage.FirstRoundF1Mid].map(
        (key) => [
          key,
          [
            {
              isLoading: isCreateRetrainingLoading,
              text: retrainText,
              level: 'primary',
              onClick: onRetrain,
            },
          ],
        ],
      ),
    ),
    ...fromPairs(
      [
        TrainingStage.FirstRoundF1Ideal,
        TrainingStage.SecondRoundF1GoUp,
        TrainingStage.SecondRoundF1GoDown,
        TrainingStage.ThirdRoundF1GoUp,
        TrainingStage.ThirdRoundF1GoDown,
      ].map((key) => [
        key,
        [
          {
            isLoading: isPublishTrainedModelLoading,
            text: publishText,
            onClick: handlePublish,
          },
          {
            isLoading: isCreateRetrainingLoading,
            text: retrainText,
            level: 'primary',
            onClick: onRetrain,
          },
        ],
      ]),
    ),
    ...fromPairs(
      [TrainingStage.SecondRoundF1Ideal, TrainingStage.ThirdRoundF1Ideal].map(
        (key) => [
          key,
          [
            {
              isLoading: isCreateRetrainingLoading,
              text: retrainText,
              onClick: onRetrain,
            },
            {
              isLoading: isPublishTrainedModelLoading,
              text: publishText,
              level: 'primary',
              onClick: handlePublish,
            },
          ],
        ],
      ),
    ),
    ...fromPairs(
      [
        TrainingStage.FourthRoundF1GoUp,
        TrainingStage.FourthRoundF1GoDown,
        TrainingStage.FourthRoundF1Ideal,
      ].map((key) => [
        key,
        [
          {
            isLoading: isPublishTrainedModelLoading,
            text: publishText,
            level: 'primary',
            onClick: handlePublish,
          },
        ],
      ]),
    ),
  };

  if (model?.stage === TrainingStage.Error) {
    return (
      <Layout direction="column" spacing={4} align="center" mt={4}>
        <Text variant="body-medium" color="status.danger">
          <FormattedMessage
            id="AutomationHubGuidedAiPage.trainingErrorLink"
            defaultMessage="An error occurred during training. Please contact support for further assistance."
            description="Title for training error customer support email link"
          />
        </Text>
      </Layout>
    );
  }

  if (!clauseId) {
    return (
      <Layout direction="column" spacing={4} align="center" mt={4}>
        <EmptyPage
          preset="new-clause"
          resolveAction={{
            variant: 'primary',
            icon: 'plus',
            iconPosition: 'left',
            text: intl.formatMessage({
              id: 'AutomationHubGuidedAiPage.newGuidedAiButton',
              defaultMessage: 'New Guided AI',
              description: 'Button text for creating a new guided AI',
            }),
            onClick: () =>
              dispatch(
                slices.automation.actions.setAutomationManageStage(
                  AutomationStageType.GuidedAiForm,
                ),
              ),
          }}
        />
      </Layout>
    );
  }

  return (
    <LoadingContainer
      isLoading={
        !trainingStatus ||
        (hasPredictionRun && !predictionStatus) ||
        !hasStageInitialized
      }
    >
      {model.stage !== TrainingStage.Predicted && (
        <>
          {!isAdmin && LABELING_TRAINING_STAGES.includes(model.stage) && (
            <Alert variant="warning" enableIcon>
              {intl.formatMessage(
                {
                  id: 'AutomationHubGuidedAiPage.limitedSampleAccessAlert',
                  defaultMessage:
                    'You have access to {availableCount} of {totalCount} samples. Mark the samples below and then contact your admin to mark the remaining samples.',
                  description:
                    'Alert for limited sample access in guided AI page. availableCount is the number of samples the user can access, totalCount is the total number of samples. Example: availableCount = 5, totalCount = 10',
                },
                {
                  availableCount: availableSamples?.length,
                  totalCount: model?.current?.unlabeledFeedback?.length,
                },
              )}
            </Alert>
          )}
          <SummaryCard
            title={TRAINING_STAGES[model?.stage]?.title}
            actions={actions[model?.stage]}
            moreActions={compact([
              shouldShowModelStatusAction && {
                text: intl.formatMessage({
                  id: 'AutomationHubGuidedAiPage.viewModelStatusButton',
                  defaultMessage: 'View Model Status',
                  description: 'Button text for viewing model status',
                }),
                onClick: () => {
                  dispatch(
                    slices.automation.actions.setAutomationManageStage(
                      AutomationStageType.ViewStatus,
                    ),
                  );
                },
              },
              shouldShowDownloadTrainingDataAction && {
                text: intl.formatMessage({
                  id: 'AutomationHubGuidedAiPage.downloadTrainingDataButton',
                  defaultMessage: 'Download Training Data',
                  description: 'Button text for downloading training data',
                }),
                onClick: () => onDownloadSamples(),
              },
              {
                text: intl.formatMessage({
                  id: 'AutomationHubGuidedAiPage.cancelTrainingButton',
                  defaultMessage: 'Cancel Training',
                  description: 'Button text for cancel training',
                }),
                onClick: () => showCancelTrainingModal(),
              },
              isRetraining && {
                level: 'danger',
                text: intl.formatMessage({
                  id: 'AutomationHubGuidedAiPage.deleteButton',
                  defaultMessage: 'Delete',
                  description: 'Button text for deleting model',
                }),
                onClick: () => deleteGuidedAiModalShow(),
              },
            ])}
            description={
              <Text>
                {TRAINING_STAGES[model?.stage]?.description}
                {TRAINING_STAGES[model?.stage]?.canAdjustPredictionFilters ? (
                  <Link onClick={() => predictionModalShow()}>
                    {intl.formatMessage({
                      id: 'AutomationHubGuidedAiPage.adjustFiltersLink',
                      defaultMessage: 'adjust your filters.',
                      description: 'Link text for adjusting filters',
                    })}
                  </Link>
                ) : null}
              </Text>
            }
          >
            <ProgressBar />
          </SummaryCard>
        </>
      )}
      <Layout direction="row" justify="space-between" w="100%" spacing={4}>
        <Box flex="1">
          {model.stage === TrainingStage.Predicted && (
            <PredictiveInfo
              onRetrainGuidedAi={retrainModalShow}
              onDownloadSamples={onDownloadSamples}
              onDeleteGuidedAi={deleteGuidedAiModalShow}
            />
          )}
          <FilterSummary />
        </Box>
        <Box flex="3">
          {model.stage === TrainingStage.Predicted && <AccuracyMetrics />}
          <SampleTable samples={samples} />
        </Box>
      </Layout>
      {predictionModal}
      {deleteGuidedAiModal}
      {retrainModal}
      {retrainGuidedAIWarning}
      {cancelTrainingModal}
    </LoadingContainer>
  );
};

export default AutomationHubGuidedAiPage;
